<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [canGoBack]="true"
      [title]="'Find New Pro Settings'" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper>

    @if (!loaded) {
      <tidy-loader style="display: flex; justify-content: center;" />
    }

    @if (loaded) {
      @if (addressList && addressList.length > 0) {
        <tidy-two-column-row class="extra-bottom-padding">
          <tidy-filter
            [label]="'Property'"
            [showLabels]="true"
            [initValue]="selectedAddressId"
            [items]="addressList"
            (optionChange)="changeProperty($event)" />
          @if (isRentalClient || hasPrivatePro) {
            <tidy-filter
              [label]="'Service Category'"
              [showLabels]="true"
              [initValue]="1"
              [items]="serviceCategoryItems"
              (optionChange)="changeServiceCategory($event)" />
          }
        </tidy-two-column-row>
      }
      @if (isLoadingForm) {
        <tidy-loader style="display: flex; justify-content: center;" />
      }
      @if (!isLoadingForm) {
        <!-- Enable/Disable Find New Pros -->
        <tidy-card>
          <form [formGroup]="form">
            <tidy-row class="card-header">
              <tidy-text [header]="'Find New Pro'" />
              <tidy-toggle
                [toggled]="allowsFindNewPros"
                (toggleChanged)="allowsFindNewPros = !allowsFindNewPros" />
            </tidy-row>
            <tidy-row>
              <tidy-text
                [body]="'The Pro Search Agent uses AI + Humans to find you a new vendor following your instructions and pricing. If there is a 100% match, it will be instantly booked, otherwise you can review quotes.'" />
            </tidy-row>
          </form>
        </tidy-card>
        @if (allowsFindNewPros) {
          <!-- Review Sources -->
          <tidy-card>
            <tidy-row class="card-header">
              <tidy-text [header]="'Review Sources'" />
            </tidy-row>
            <tidy-row class="extra-bottom-padding">
              <tidy-text [body]="'What review sources do you want to accept?'" />
            </tidy-row>
            <div class="two-column-grid">
              @for (source of backendReviewSources; track source.key) {
                <tidy-checkbox
                  [(checked)]="source.checked"
                  [items]="[{value: source.key, viewValue: source.label}]"
                  [initValue]="source.checked" />
              }
              @for (source of sourceNetworks; track source.value) {
                <div (click)="gateLockedSetting()">
                  <tidy-checkbox
                    [checked]="false"
                    [items]="[source]"
                    style="pointer-events: none" />
                </div>
              }
            </div>
          </tidy-card>

          <!-- Compliance Requirements Section -->
          <tidy-card>
            <tidy-row class="card-header">
              <tidy-text [header]="'Compliance Requirements'" />
            </tidy-row>

            <div class="two-column-grid">
              <!-- Backend compliance items -->
              @for (item of backendComplianceItems; track item.key) {
                <tidy-checkbox
                  [(checked)]="item.checked"
                  [items]="[{value: item.key, viewValue: item.label}]"
                  [initValue]="item.checked" />
              }

              <!-- Equipment (not supported by backend) -->
              <div (click)="gateLockedSetting()">
                <tidy-checkbox
                  [checked]="false"
                  [items]="[{value: 'equipment', viewValue: 'Equipment'}]"
                  style="pointer-events: none" />
              </div>

              <!-- Hardcoded compliance items (not supported by backend) -->
              @for (item of complianceItems; track item.value) {
                <div (click)="gateLockedSetting()">
                  <tidy-checkbox
                    [checked]="false"
                    [items]="[item]"
                    style="pointer-events: none" />
                </div>
              }
            </div>

            <!-- Non-compliant handling -->
            <div (click)="gateLockedSetting()">
              <tidy-row class="extra-top-padding">
                <tidy-select
                  [label]="'Non-Compliant Pro Handling'"
                  [ngModel]="nonCompliantHandling"
                  [items]="nonCompliantOptions"
                  style="pointer-events: none" />
              </tidy-row>
            </div>
          </tidy-card>

          <!-- Gender Preference -->
          <tidy-card>
            <form [formGroup]="form">
              <tidy-row class="card-header">
                <tidy-text [header]="'Gender Preference'" />
              </tidy-row>
              <tidy-row>
                <tidy-select
                  [label]="'Preferred Gender'"
                  [form]="form.controls.gender"
                  formControlName="gender"
                  [items]="genderOptions" />
              </tidy-row>
            </form>
          </tidy-card>

          <!-- Budget Preference -->
          <tidy-card>
            <tidy-row class="card-header">
              <tidy-text [header]="'Budget Preference'" />
            </tidy-row>
            <tidy-row class="extra-bottom-padding">
              <tidy-text [body]="'What\'s your priority when finding a pro?'" />
            </tidy-row>
            <form [formGroup]="settingsForm">
              <div (click)="gateLockedSetting()">
                <tidy-row>
                  <tidy-radio-button
                    [form]="settingsForm.controls.budgetPreference"
                    formControlName="budgetPreference"
                    [items]="budgetPreferenceOptions"
                    style="pointer-events: none" />
                </tidy-row>
              </div>
            </form>
          </tidy-card>

          <!-- Quality Standards -->
          <tidy-card>
            <tidy-row class="card-header">
              <tidy-text [header]="'Quality Standards'" />
            </tidy-row>
            <form [formGroup]="settingsForm">
              <div (click)="gateLockedSetting()">
                <tidy-row>
                  <tidy-select
                    [label]="'Minimum Star Rating'"
                    [form]="settingsForm.controls.minRating"
                    formControlName="minRating"
                    [items]="ratingOptions"
                    style="pointer-events: none" />
                </tidy-row>
                <tidy-row>
                  <tidy-select
                    [label]="'Minimum Review Count'"
                    [form]="settingsForm.controls.minReviews"
                    formControlName="minReviews"
                    [items]="reviewCountOptions"
                    style="pointer-events: none" />
                </tidy-row>
                <tidy-row>
                  <tidy-select
                    [label]="'Years of Experience'"
                    [form]="settingsForm.controls.yearsExperience"
                    formControlName="yearsExperience"
                    [items]="yearsExperienceOptions"
                    style="pointer-events: none" />
                </tidy-row>
              </div>
            </form>
          </tidy-card>

          <!-- Instant Pricing Book -->
          @if (services) {
            <tidy-card>
              <tidy-row class="card-header">
                <tidy-text [header]="'Instant Pricing Book'" />
              </tidy-row>
              <tidy-row class="extra-bottom-padding">
                <tidy-text
                  [body]="'If a pro matches your criteria and accepts your price, we will book them automatically. Otherwise, we will share quotes with you for approval.'" />
              </tidy-row>
              <form [formGroup]="pricingForm">
                <tidy-row>
                  <tidy-text [body]="'Select Frequency'" />
                </tidy-row>
                <tidy-row class="extra-bottom-padding">
                  <tidy-radio-button
                    formControlName="frequency"
                    [form]="pricingForm.controls.frequency"
                    [items]="frequencies" />
                </tidy-row>
                <tidy-row>
                  <tidy-text [body]="'Select Billing Preference'" />
                </tidy-row>
                <tidy-row class="extra-bottom-padding">
                  <tidy-radio-button
                    formControlName="chargeType"
                    [form]="pricingForm.controls.chargeType"
                    [items]="billingPreference" />
                </tidy-row>
              </form>
              <tidy-row class="extra-bottom-padding">
                <tidy-divider />
              </tidy-row>
              <tidy-row class="extra-bottom-padding">
                <tidy-text [body]="'<b>Your Current Instant Book Prices</b>'" />
              </tidy-row>
              @for (service of services; track service) {
                <tidy-row [alignLastItemRight]="true">
                  <tidy-text [body]="service?.name" />
                  <tidy-text [body]="getPrice(service)" />
                </tidy-row>
              }
              @if (selectedCategory?.value == 1) {
                <tidy-row [align]="'center'">
                  <tidy-text
                    (action)="goToEditPricesPage()"
                    [body]="'Edit Prices'"
                    class="link" />
                </tidy-row>
              }
            </tidy-card>
          }
        }
        @if (errorMessage) {
          <tidy-error-message
            [text]="errorMessage" />
        }
        <tidy-button
          [text]="'Save Settings'"
          [action]="boundSave"
          class="primary" />
      }
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
